"use client";

import { AssetBlok, sizes } from "@/components/bloks/Asset";
import BlokWrap from "@/components/bloks/BlokWrap";
import { RichTextRender } from "@/components/ui/RichTextRender";
import { useGlobal } from "@/features/global/GlobalContext";
import type { BlokEmailSubscription } from "@/storyblok";
import type { BlokProps } from "@frend-digital/storyblok";
import styles from "./EmailSubscription.module.css";
import { EmailSubscriptionForm } from "./EmailSubscriptionForm";

const darkenColor = (color: string, amount = 0.15): string => {
	if (!color) return "";

	const hex = color.replace("#", "");
	const r = Number.parseInt(hex.substring(0, 2), 16);
	const g = Number.parseInt(hex.substring(2, 4), 16);
	const b = Number.parseInt(hex.substring(4, 6), 16);

	const newR = Math.max(0, Math.floor(r * (1 - amount)));
	const newG = Math.max(0, Math.floor(g * (1 - amount)));
	const newB = Math.max(0, Math.floor(b * (1 - amount)));

	return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
};

export const EmailSubscriptionBlok = ({ blok, preview }: BlokProps<BlokEmailSubscription>) => {
	const {
		data: { colorMap },
	} = useGlobal();

	const colorEntry = colorMap[blok.backgroundColor!] || "";
	const darkerBgColor = darkenColor(colorEntry.color);

	return (
		<BlokWrap editable={blok} as="section" className={styles.root}>
			<div
				className={styles.container}
				style={{
					backgroundColor: colorEntry.color,
					color: colorEntry.foreground,
					["--input-border-color" as string]: darkerBgColor,
				}}
			>
				{blok.media && blok.media.length > 0 && (
					<div className={styles.imageWrapper}>
						<AssetBlok source={blok.media} sizes={sizes(100, 50, 50)} />
					</div>
				)}
				<div className={styles.content}>
					{blok.title && <h2 className={styles.title}>{blok.title}</h2>}
					{blok.description && (
						<div className={styles.description}>
							<RichTextRender blok={blok.description} preview={preview} />
						</div>
					)}
					<EmailSubscriptionForm terms={blok.terms} preview={preview} />
				</div>
			</div>
		</BlokWrap>
	);
};
